import { useState } from 'react'
import { Alert, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import type { PerfQueryRow, QuerySort } from '@api/types/performance'
import { SqlBlock } from './SqlBlock'
import { formatCount, formatMs, latencySeverity } from './format'

interface QueriesTabProps {
  rows?: PerfQueryRow[]
  loading: boolean
  error?: string | null
  onRetry: () => void
  sort: QuerySort
  onSortChange: (sort: QuerySort) => void
}

const SORTS: { value: QuerySort; label: string }[] = [
  { value: 'total', label: 'Total time consumed' },
  { value: 'avg', label: 'Average duration' },
  { value: 'p95', label: 'p95 duration' },
  { value: 'max', label: 'Slowest single call' },
  { value: 'count', label: 'Call count' },
  { value: 'errors', label: 'Failures' },
]

/**
 * Query shapes as the API measured them.
 *
 * Values are stripped out before anything is stored, so these are shapes rather than statements —
 * which is both what makes the grouping meaningful and what keeps an email address out of a
 * monitoring table.
 */
export function QueriesTab({ rows, loading, error, onRetry, sort, onSortChange }: QueriesTabProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const columns: DataTableColumn<PerfQueryRow>[] = [
    {
      key: 'sql',
      header: 'Query',
      primary: true,
      width: '45%',
      render: (row) => (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <SqlBlock sql={row.sql} maxLines={3} />
          {row.topRoute && (
            <Chip
              label={`mostly from ${row.topRoute}`}
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'flex-start', fontSize: 10, maxWidth: '100%' }}
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'count',
      header: 'Calls',
      align: 'right',
      render: (row) => formatCount(row.count),
    },
    {
      key: 'avg',
      header: 'Average',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" color={`${latencySeverity(row.avgMs)}.main`} fontWeight="bold">
          {formatMs(row.avgMs)}
        </Typography>
      ),
    },
    {
      key: 'p95',
      header: 'p95',
      align: 'right',
      hideBelow: 'md',
      render: (row) => formatMs(row.p95Ms),
    },
    {
      key: 'max',
      header: 'Max',
      align: 'right',
      hideBelow: 'md',
      render: (row) => formatMs(row.maxMs),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      // The honest ranking. A 2ms query called half a million times is the expensive one.
      render: (row) => (
        <Typography variant="body2" fontWeight="bold">
          {formatMs(row.totalMs)}
        </Typography>
      ),
    },
    {
      key: 'errors',
      header: 'Failed',
      align: 'right',
      hideBelow: 'lg',
      render: (row) =>
        row.errorCount === 0 ? (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ) : (
          <Typography variant="body2" color="error.main">
            {formatCount(row.errorCount)}
          </Typography>
        ),
    },
    {
      key: 'service',
      header: 'Service',
      hideBelow: 'lg',
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.service}
        </Typography>
      ),
    },
  ]

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        Measured from the API, so these include connection and network wait that Postgres never sees, and
        cover only what these two API processes issued. The Database tab is the server's own view and also
        covers the workers and the importers.
      </Alert>

      <FormControl
        size="small"
        sx={{ minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' }, alignSelf: 'flex-start' }}
      >
        <InputLabel id="query-sort">Rank by</InputLabel>
        <Select
          labelId="query-sort"
          label="Rank by"
          value={sort}
          onChange={(e) => onSortChange(e.target.value as QuerySort)}
        >
          {SORTS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DataTable
        rows={rows ?? []}
        columns={columns}
        getRowKey={(row) => `${row.service}|${row.fingerprint}`}
        loading={loading}
        error={error}
        onRetry={onRetry}
        emptyMessage="No queries recorded in this window."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </Stack>
  )
}
